<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { PropType, onMounted, computed } from 'vue'
import PostModel from '~/models/Discussion/Post'
import TheRadio from '~/components/ui/TheRadio.vue'
import { useBasketStore } from '~/stores/basket'
const trans = useI18n().t

onMounted(() => {
    clearReportForm()
})

const userAuthEmail = computed(() => {
    return useBasketStore().authUser?.email || ''
})

const userEmail = ref(userAuthEmail.value || '')

const props = defineProps({
    post: {
        type: Object as PropType<PostModel>,
        default: null,
    },
})

const reasons = {
    reportReasonInappropriate: trans('discussions.reasons.reportReasonInappropriate'),
    reportReasonInaccurate: trans('discussions.reasons.reportReasonInaccurate'),
    reportReasonCopyright: trans('discussions.reasons.reportReasonCopyright'),
    reportReasonDuplicate: trans('discussions.reasons.reportReasonDuplicate'),
    reportReasonOther: trans('discussions.reasons.reportReasonOther'),
}

const data = reactive({
    reportFormSubmitted: false,
    reportFormLoading: false,
    reportFormReason: '',
    reportFormMessage: '',
    reportFormErrors: {} as { reason?: string; message?: string; email?: string },
    reportResponseError: '',
})

const submitReportForm = async () => {
    data.reportFormLoading = true
    data.reportResponseError = ''
    data.reportFormSubmitted = true

    if (!isReportFormValid()) {
        return false
    }

    const requestBody = {
        postId: props.post.id,
        reason: data.reportFormReason,
        message: data.reportFormMessage,
        email: userEmail.value,
    }

    try {
        await useNuxtApp().$repository.discussionReport(requestBody)
        data.reportFormLoading = false
        clearReportForm()
        useNuxtApp().$bus.$emit('showModal', 'thank-you-discussions')
    } catch (e) {
        if (e instanceof Error) {
            data.reportFormLoading = false
            data.reportResponseError = e.message || 'Something went wrong'
        }
    }

    return true
}

const isReportFormValid = () => {
    data.reportFormErrors = {}

    if (!data.reportFormSubmitted) {
        return
    }

    if (data.reportFormReason === '') {
        data.reportFormErrors.reason = trans('discussions.formValidatorReasonEmpty')
    }

    if (data.reportFormMessage === '') {
        data.reportFormErrors.message = trans('discussions.formValidatorTextEmpty')
    }

    if (userEmail.value === '') {
        data.reportFormErrors.email = trans('discussions.formValidatorEmailEmpty')
    }

    return Object.keys(data.reportFormErrors).length === 0
}

const clearReportForm = () => {
    data.reportFormSubmitted = false
    data.reportFormLoading = false
    data.reportFormErrors = {}
    data.reportFormReason = ''
    data.reportFormMessage = ''
    data.reportResponseError = ''
}
</script>

<template>
    <Modal size="lg" :modal-name="'discussion-report-post-' + post.id" :title="$t('discussions.reason')">
        <template v-if="data.reportFormSubmitted && data.reportFormLoading !== true">
            <div v-if="data.reportResponseError !== ''" class="discussion-report__response error">
                {{ $t('somethingWentWrong') }}
            </div>
            <div v-else class="discussion-report__response success">
                {{ $t('discussions.successfullyReported') }}
            </div>
        </template>
        <form @submit.prevent="submitReportForm">
            <div v-for="(reason, key) in reasons" :key="key" class="discussion-report__reason">
                <TheRadio :id="key" v-model:checked="data.reportFormReason" :value="reason" :label="reason" />
            </div>
            <div v-if="data.reportFormErrors.reason" class="text-danger">{{ data.reportFormErrors.reason }}</div>

            <textarea
                v-model="data.reportFormMessage"
                class="discussion-report__textarea form-control form-control-sm mt-4"
                name="text"
                :placeholder="$t('discussions.messageReport')"
                cols="30"
                rows="5"
            ></textarea>
            <div v-if="data.reportFormErrors.message" class="text-danger">{{ data.reportFormErrors.message }}</div>

            <label for="reportEmail" class="fs-12 mt-3">{{ $t('discussions.yourMail') }}</label>
            <input id="reportEmail" v-model="userEmail" type="email" name="email" class="form-control" :disabled="userAuthEmail !== ''" />
            <div v-if="data.reportFormErrors.email" class="text-danger">{{ data.reportFormErrors.email }}</div>

            <div class="text-center">
                <button class="btn btn-primary btn-rounded-circle btn-sm py-1 px-4 mt-3" type="submit">
                    {{ $t('discussions.report') }}
                </button>
            </div>
        </form>
    </Modal>
</template>

<style lang="scss" scoped>
.discussion-report {
    &__reason {
        margin-bottom: 10px;
    }

    &__textarea {
        width: 100%;
        resize: vertical;

        &:focus {
            outline: none !important;
            border-color: $color-cta;
            box-shadow: 0 0 10px $color-cta-extra-light;
        }
    }

    &__response {
        text-align: center;
        padding: 0.5rem 1rem;
        margin: 0.5rem 0;

        &.success {
            background-color: $color-green;
            color: $color-green-dark;
            border-radius: 50px;
        }

        &.error {
            color: $color-alert;
            background-color: $color-alert-light;
            border-radius: 50px;
        }
    }
}
.post {
    position: relative;
    padding-top: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;

    &__product {
        display: inline-flex;
        flex-direction: column;

        &__name {
            border-radius: 100px;
            background: $color-grey;
            color: $color-dark;
            padding: 9px 20px 9px 13px;
            display: flex;
            align-items: center;
            transition: all 0.2s;
            font-size: 14px;
            margin-bottom: 19px;
            width: fit-content;

            &:hover {
                color: $color-cta;
            }
        }

        &__img {
            width: 47px;
            height: 38px;
            display: flex;
            justify-content: center;
            align-items: center;
        }
    }

    &__report {
        position: absolute;
        top: 0;
        right: 0;
        font-size: 12px;
        color: $color-grey-semi-dark;
        transition: all 0.2s;
        margin-top: 1rem;

        &:hover {
            color: $color-grey-extra-dark;
        }
    }

    &__date {
        font-size: 12px;
        color: $color-dark;
    }

    &__title {
        font-size: 18px;
        color: $color-dark;
        margin: 1rem 0 0;

        @include screen-md {
            font-weight: normal;
        }
    }

    &__description {
        font-size: 14px;
        line-height: 20px;
    }
}
</style>
